/**
 * Popup with 10 % off the first order in exchange for an email address (snippets/vindely-popup.liquid).
 *
 * Opens when the visitor has been on the site for 8 seconds in this session, and only while no other
 * dialog, no cookie banner and no background tab is in the way. Closed = hidden for 14 days,
 * signed up = hidden for good. The popup closes on submit; Shopify's customer form then reloads
 * the page and the popup reopens with the code (or with the error message).
 * Not shown in a session that started with a Google Ads click (gclid, gbraid or wbraid), Elias 23/10.
 */
const STORAGE_KEY = 'vindely-popup';
const SENT_KEY = 'vindely-popup-sent';
const START_KEY = 'vindely-popup-start';
const DELAY = 8000;
const RETRY = 2000;
const MAX_TRIES = 30;
const PAUSE = 14 * 24 * 60 * 60 * 1000;
const AD_KEY = 'vindely-popup-ad';
const AD_PARAMS = ['gclid', 'gbraid', 'wbraid'];

/** Storage throws in some private windows; the popup then only forgets earlier choices. */
function read(store, key) {
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
}

function write(store, key, value) {
  try {
    if (value === null) window[store].removeItem(key);
    else window[store].setItem(key, value);
  } catch {
    // Nothing to remember without storage.
  }
}

class VindelyPopup extends HTMLElement {
  #timer = 0;
  #tries = 0;

  get #dialog() {
    return this.querySelector('dialog');
  }

  connectedCallback() {
    this.#fillAmounts();
    this.#dialog.addEventListener('click', this.#onClick);
    this.#dialog.addEventListener('close', this.#onClose);
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);

    if (this.#afterSignup() || this.#paused() || this.#fromAd()) return;
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  /** True when this page load is the reload after our own signup. */
  #afterSignup() {
    if (!read('sessionStorage', SENT_KEY)) return false;
    write('sessionStorage', SENT_KEY, null);

    const posted = this.querySelector('[data-popup-posted]')?.dataset.popupPosted;
    const url = new URL(window.location.href);
    const success = posted === 'true' || url.searchParams.get('customer_posted') === 'true';
    if (url.searchParams.has('customer_posted')) {
      url.searchParams.delete('customer_posted');
      url.hash = '';
      history.replaceState(history.state, '', url);
    }

    if (success) {
      write('localStorage', STORAGE_KEY, 'joined');
      this.#open('joined');
      return true;
    }
    if (posted === 'error') {
      this.#open('form');
      return true;
    }
    return false;
  }

  /** Visitors from a Google Ads click see the product without the popup for the whole session. */
  #fromAd() {
    const params = new URL(window.location.href).searchParams;
    if (AD_PARAMS.some((name) => params.has(name))) write('sessionStorage', AD_KEY, '1');
    return read('sessionStorage', AD_KEY) === '1';
  }

  #paused() {
    const value = read('localStorage', STORAGE_KEY);
    if (value === 'joined') return true;
    const closedAt = Number(value?.split(':')[1]);
    return closedAt > 0 && Date.now() - closedAt < PAUSE;
  }

  /** Counts from the first page of the session, so quick browsing still reaches 8 seconds. */
  #schedule() {
    const now = Date.now();
    const start = Number(read('sessionStorage', START_KEY)) || now;
    write('sessionStorage', START_KEY, String(start));
    this.#timer = setTimeout(this.#tryOpen, Math.max(0, DELAY - (now - start)));
  }

  #tryOpen = () => {
    if (this.#paused()) return;
    if (this.#busy()) {
      this.#tries += 1;
      if (this.#tries < MAX_TRIES) this.#timer = setTimeout(this.#tryOpen, RETRY);
      return;
    }
    this.#open('form');
  };

  #busy() {
    if (document.hidden || document.querySelector('dialog[open]')) return true;
    const banner = document.getElementById('shopify-pc__banner');
    return Boolean(banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden');
  }

  /** @param {'form' | 'joined'} step */
  #open(step) {
    for (const element of this.querySelectorAll('[data-popup-step]')) {
      element.hidden = element.dataset.popupStep !== step;
    }
    this.#dialog.setAttribute('aria-labelledby', step === 'joined' ? 'VindelyPopupJoined' : 'VindelyPopupHeading');
    if (!this.#dialog.open) this.#dialog.showModal();
    // Focus the dialog itself, so the close button shows no focus ring for mouse users.
    this.#dialog.focus({ preventScroll: true });
  }

  /** Shopify's hCaptcha can show a challenge over the page; a modal dialog in the top layer would hide it. */
  #onSubmit = () => {
    write('sessionStorage', SENT_KEY, '1');
    this.#dialog.close();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target === this.#dialog || target.closest('[data-popup-close]')) {
      this.#dialog.close();
      return;
    }
    const copy = target.closest('[data-popup-copy]');
    if (copy instanceof HTMLElement) this.#copy(copy);
  };

  #onClose = () => {
    if (read('localStorage', STORAGE_KEY) !== 'joined') write('localStorage', STORAGE_KEY, `closed:${Date.now()}`);
  };

  /** @param {HTMLElement} button */
  async #copy(button) {
    const code = this.querySelector('[data-popup-code]');
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code.textContent.trim());
      button.textContent = button.dataset.copied || button.textContent;
    } catch {
      const range = document.createRange();
      range.selectNodeContents(code);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  }

  /**
   * The minimum is 400 SEK and Shopify converts it with the market's rate. The amount is rounded up
   * (to whole units below 100, else to tens), so an order of the shown amount always qualifies.
   */
  #fillAmounts() {
    const { active = 'SEK', rate = '1' } = window.Shopify?.currency ?? {};
    const exact = Math.round(Number(this.dataset.minimum) * Number(rate) * 100) / 100;
    const unit = exact >= 100 ? 10 : 1;
    const value = Math.ceil(exact / unit) * unit;
    let text = `${value} ${active}`;
    try {
      text = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: active,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      // Unknown currency for Intl: keep «value CODE».
    }
    for (const span of this.querySelectorAll('[data-popup-amount]')) span.textContent = text;
  }
}

if (!customElements.get('vindely-popup')) customElements.define('vindely-popup', VindelyPopup);
